import React, { useEffect, useState } from "react";
import { useChatClient } from "@/components/pages/chats/hooks/useChatClient";
import { useHotel } from "@/hooks/useHotel";
import { Channel, ChannelFilters, MessageResponse } from "stream-chat";
import { format } from "date-fns";
import { ArrowDown, ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";

interface ChatSectionProps {
  chatId?: string;
  bookingId?: string;
  guestId?: string;
  conciergeName?: string;
}

export const ChatSection: React.FC<ChatSectionProps> = ({
  chatId,
  bookingId,
  guestId,
  conciergeName,
}) => {
  const { t } = useTranslation();
  const { capabilities } = useAccessCapabilities();
  const { hotelId } = useHotel();
  const { client, isLoading: isChatLoading } = useChatClient(
    hotelId ?? null,
    capabilities.canUseGuestCommunication,
  );
  const [channel, setChannel] = useState<Channel | null>(null);
  const [messages, setMessages] = useState<MessageResponse[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!client || !hotelId) return;

    const fetchChannel = async () => {
      setIsLoading(true);
      try {
        let targetChannel: Channel | null = null;

        if (chatId) {
          targetChannel = client.channel("messaging", chatId);
        } else if (bookingId && guestId) {
          const hotelChatId = `hotel_${hotelId}`;
          const guestChatId = `guest_${guestId}`;
          const allUsers = [hotelChatId, guestChatId];

          const channels = await client.queryChannels(
            {
              type: "messaging",
              members: { $eq: allUsers },
              bookingId,
            } as ChannelFilters,
            {},
            { watch: true },
          );

          if (channels.length > 0) {
            targetChannel = channels[0];
          }
        }

        if (targetChannel) {
          setChannel(targetChannel);
          const state = await targetChannel.watch();
          setMessages(state.messages || []);
        }
      } catch (error) {
        console.error("Failed to fetch chat channel:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchChannel();
  }, [client, hotelId, chatId, bookingId, guestId]);

  if (!capabilities.canUseGuestCommunication) {
    return null;
  }

  if (isChatLoading || isLoading)
    return (
      <div className="text-sm text-gray-400">
        {t("feedbacks.chat.loadingChats")}
      </div>
    );
  if (!channel) return null;

  const firstMessage = messages[0];

  return (
    <div className="space-y-4 border-t border-gray-100 pt-4">
      <div
        className="group flex cursor-pointer items-center gap-2"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-gray-5 text-sm font-medium transition-colors group-hover:text-black">
          {t("feedbacks.modal.openChats", "Open Chats")}
        </span>
        <ArrowDown height={14} width={14} />
      </div>

      {isOpen && (
        <div className="animate-in fade-in slide-in-from-top-2 space-y-6 duration-200">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            <span className="font-medium text-black">
              {t("feedbacks.chat.openConversation")}
            </span>
            <span>|</span>
            <span>
              {firstMessage?.created_at
                ? format(
                    new Date(firstMessage.created_at),
                    "h:mm a, d MMMM yyyy",
                  )
                : ""}
            </span>
            <span>|</span>
            <span>{conciergeName || ""}</span>
          </div>

          <div className="space-y-6">
            {(messages || []).slice(0, 1).map((msg) => {
              const msgInitials = (msg.user?.name || msg.user?.id || "")
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2);

              return (
                <div key={msg.id} className="flex flex-col gap-2">
                  <div className="flex items-end gap-3">
                    <div className="flex-1 rounded-3xl rounded-bl-none bg-gray-100 p-5">
                      <p className="text-[15px] leading-relaxed whitespace-pre-wrap text-gray-800">
                        {msg.text}
                      </p>
                    </div>
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-400 text-sm font-medium text-white">
                      {msgInitials}
                    </div>
                  </div>
                  <div className="ml-2 flex items-center gap-2 text-[11px] text-gray-400">
                    <span>
                      {msg.created_at
                        ? format(new Date(msg.created_at), "h:mm a")
                        : ""}
                    </span>
                    <span>•</span>
                    <span>{t("feedbacks.chat.sms")}</span>
                    <span>•</span>
                    <span className="text-gray-300">✓</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
